import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';
import { useNotificationStore } from '@/stores/notificationStore';
import { ResearchService, type ShortlistWithVenue } from '@/services/ResearchService';
import { DollarSign } from 'lucide-react';

interface VenueCostOverrideModalProps {
  isOpen: boolean;
  onClose: () => void;
  shortlistItem: ShortlistWithVenue | null;
  onSave: () => void;
}

export function VenueCostOverrideModal({
  isOpen,
  onClose,
  shortlistItem,
  onSave,
}: VenueCostOverrideModalProps) {
  const [lowestRoomRate, setLowestRoomRate] = useState<string>('');
  const [totalCost, setTotalCost] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const notificationStore = useNotificationStore();
  const researchService = new ResearchService();

  // Initialize form values when modal opens or shortlistItem changes
  useEffect(() => {
    if (isOpen && shortlistItem) {
      setLowestRoomRate(
        shortlistItem.override_lowest_room_rate?.toString() || ''
      );
      setTotalCost(
        shortlistItem.override_total_cost?.toString() || ''
      );
    }
  }, [isOpen, shortlistItem]);

  const handleSave = async () => {
    if (!shortlistItem) return;

    try {
      setIsSaving(true);

      const updateData: {
        override_lowest_room_rate?: number | null;
        override_total_cost?: number | null;
      } = {};

      // Parse and set lowest room rate override
      if (lowestRoomRate.trim() === '') {
        updateData.override_lowest_room_rate = null;
      } else {
        const parsedRate = parseFloat(lowestRoomRate);
        if (isNaN(parsedRate) || parsedRate < 0) {
          notificationStore.error('Lowest room rate must be a valid positive number', {
            title: 'Validation Error',
          });
          return;
        }
        updateData.override_lowest_room_rate = parsedRate;
      }

      // Parse and set total cost override
      if (totalCost.trim() === '') {
        updateData.override_total_cost = null;
      } else {
        const parsedCost = parseFloat(totalCost);
        if (isNaN(parsedCost) || parsedCost < 0) {
          notificationStore.error('Total cost must be a valid positive number', {
            title: 'Validation Error',
          });
          return;
        }
        updateData.override_total_cost = parsedCost;
      }

      await researchService.updateShortlistItem(shortlistItem.id, updateData);
      
      notificationStore.success('Venue costs updated successfully', {
        title: 'Success',
      });
      
      onSave();
      onClose();
    } catch (error) {
      console.error('Failed to update venue costs:', error);
      notificationStore.error('Failed to update venue costs', {
        title: 'Error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form to original override values
    if (shortlistItem) {
      setLowestRoomRate(
        shortlistItem.override_lowest_room_rate?.toString() || ''
      );
      setTotalCost(
        shortlistItem.override_total_cost?.toString() || ''
      );
    }
    onClose();
  };

  if (!shortlistItem) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Edit Venue Costs"
      description={`Override costs for ${shortlistItem.venue.property_name}`}
      size="md"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Input
            label="Lowest Room Rate"
            type="number"
            step="0.01"
            min="0"
            placeholder="Enter lowest room rate per night"
            value={lowestRoomRate}
            onChange={(e) => setLowestRoomRate(e.target.value)}
            leftIcon={<DollarSign className="w-4 h-4" />}
            hint="Enter the lowest room rate per night (USD)"
          />
        </div>

        <div className="space-y-2">
          <Input
            label="Total Cost"
            type="number"
            step="0.01"
            min="0"
            placeholder="Enter total cost"
            value={totalCost}
            onChange={(e) => setTotalCost(e.target.value)}
            leftIcon={<DollarSign className="w-4 h-4" />}
            hint="Enter the total cost for the venue (USD)"
          />
        </div>

        <div className="bg-gray-50 rounded-lg p-3 text-sm text-muted-foreground">
          <p className="font-medium mb-1">Note:</p>
          <p>
            These values will override the automatic costs from Cvent proposals and will be displayed in the proposal's "Where to Stay" section.
            Leave fields empty to clear overrides and use the original values.
          </p>
          {(shortlistItem.cvent_lowest_room_rate || shortlistItem.cvent_total_cost) && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <p className="text-xs font-medium mb-1">Original values:</p>
              {shortlistItem.cvent_lowest_room_rate && (
                <p className="text-xs">Lowest room rate: ${shortlistItem.cvent_lowest_room_rate.toLocaleString()}/night</p>
              )}
              {shortlistItem.cvent_total_cost && (
                <p className="text-xs">Total cost: ${shortlistItem.cvent_total_cost.toLocaleString()}</p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t">
        <Button variant="ghost" onClick={handleCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} isLoading={isSaving}>
          Save Changes
        </Button>
      </div>
    </Modal>
  );
}

